import ObjectRender from '@common/screens/RenderScreen/ObjectRender';
import { ComponentType, ElementType, IGroup } from '@common/types/element';
import { isEmpty, isNil, map } from 'lodash';
import React, { FC, useMemo } from 'react';
import { TouchableOpacity, View } from 'react-native';
import createStyles from './style';
import GroupScrollableWrapper from '@common/components/Group/components/Scrollable';
import { getActions } from '@common/utils/handleActions/func/helps';

const CusGroup: FC<IGroup> = (attrs) => {
  const styles = createStyles(attrs);

  const {
    children,
    isInCustomList,
    scroll,
    onPress,
    clipContent,
    clipMainBackground,
  } = attrs;

  const mapElementChild = (child: ElementType) => {
    const groupActionId = attrs?.actions && Object.keys(attrs.actions)[0];
    const allowListAction = isEmpty(child?.actions) && !isEmpty(groupActionId);
    const selectedItem = {
      itemId: attrs.selectedItem?._id,
      tableId: attrs.selectedItem?.tableId,
      itemIndex: attrs.selectedItem?.itemIndex,
    };

    const mappedObject = {
      ...child,
      ...(isNil(child.actions) || isEmpty(child.actions)
        ? { actions: attrs.actions }
        : {
            actions: {
              ...child?.actions,
              ...attrs?.actions,
            },
          }),
      currentScroll: {
        active: true,
        arrange: scroll?.arrange,
      },
      selectedItem,
      record: attrs.record,
      groupActionId,
      currentListIds: { ...(attrs.currentListIds || {}) },
      currentRecordIds: { ...(attrs.currentRecordIds || {}) },
      currentListId: attrs?.currentListId || attrs.id,
      ...(isInCustomList && {
        initId: `${child.id}`,
      }),
      allowListAction,
      screenUuid: attrs?.screenUuid,
    };

    return mappedObject;
  };

  const newChildren = useMemo(
    () =>
      map(children, (child) => {
        const isFixedElement =
          (child.fixPosition === 'top' || child.fixPosition === 'bottom') &&
          child.type === 'section';
        if (
          (isFixedElement && clipContent) ||
          (clipMainBackground && clipMainBackground == child.id)
        ) {
          return null;
        }
        return mapElementChild(child);
      }).filter(Boolean) as ElementType[],
    [children, attrs.record, attrs?.actions, attrs.selectedItem]
  );

  const fixedElements = useMemo(
    () =>
      map(children, (child) => {
        const isFixedElement =
          (child.fixPosition === 'top' || child.fixPosition === 'bottom') &&
          child.type === 'section';
        if (clipMainBackground) {
          const isMainBG = clipMainBackground == child.id;
          if (isMainBG) {
            child.fixPosition = 'top';
            child.height = attrs.height;
            child.width = attrs.width;
            child.x = attrs.x;
            child.y = attrs.y;
            return mapElementChild(child);
          } else return null;
        } else {
          if (!isFixedElement) {
            return null;
          }
          return mapElementChild(child);
        }
      }).filter(Boolean) as ElementType[],
    [children, attrs.record, attrs?.actions, attrs.selectedItem]
  );

  // Re-calculate section height
  const arrCom = useMemo(() => {
    if (!isInCustomList) {
      return newChildren;
    }
    const sectionGroup: Array<any> = [];
    if (
      newChildren.length > 1 &&
      newChildren[0].type == ComponentType.section
    ) {
      let newSectionElement = newChildren[0];
      const sectionChildren: Array<any> = [];
      let lastBottomLeft = 0;
      for (let i = 1; i < newChildren.length; i++) {
        const o = newChildren[i];
        const currentBottomLeft = o.y + o.height;
        if (
          o.y >= newSectionElement.y &&
          currentBottomLeft <= newSectionElement.y + newSectionElement.height
        ) {
          sectionChildren.push(o);
          if (currentBottomLeft > lastBottomLeft) {
            lastBottomLeft = currentBottomLeft;
          }
        } else {
          sectionGroup.push(o);
        }
      }
      if (lastBottomLeft) {
        (newSectionElement as any).paddingBottom =
          newSectionElement.y + newSectionElement.height - lastBottomLeft;
      }
      newSectionElement.children = sectionChildren;
      sectionGroup.push(newSectionElement);
      return sectionGroup;
    }
    return newChildren;
  }, [newChildren]);

  if (children.length == 0) return <React.Fragment />;

  const layout = {
    offsetTop: attrs?.y,
    offsetLeft: attrs?.x,
    parentWidth: attrs?.width,
  };
  const emptyAction = isEmpty(attrs.actions);
  if (clipContent) {
    return (
      <>
        <GroupScrollableWrapper
          attrs={attrs}
          scroll={attrs.scroll}
          clipContent={clipContent}
          fixedElements={fixedElements}
        >
          {emptyAction ? (
            <>
              <View style={{ ...styles.container }}>
                <ObjectRender
                  keyItem={attrs?.selectedItem?.itemIndex}
                  arrComp={arrCom}
                  isScreen={false}
                  layout={{
                    offsetLeft: attrs.x,
                    offsetTop: attrs.y,
                    parentWidth: attrs?.width,
                  }}
                />
              </View>
            </>
          ) : (
            <View style={styles.container}>
              <TouchableOpacity
                onPress={() => {
                  onPress(getActions(attrs.actions), {});
                }}
                style={{
                  minHeight: attrs.height,
                  width: attrs.width,
                }}
                activeOpacity={1}
              >
                <ObjectRender
                  keyItem={attrs?.selectedItem?.itemIndex}
                  arrComp={arrCom}
                  isScreen={false}
                  layout={layout}
                />
              </TouchableOpacity>
            </View>
          )}
        </GroupScrollableWrapper>
      </>
    );
  } else {
    return (
      <>
        <GroupScrollableWrapper attrs={attrs} scroll={attrs.scroll}>
          {emptyAction ? (
            <View style={styles.container}>
              <ObjectRender
                keyItem={attrs?.selectedItem?.itemIndex}
                arrComp={arrCom}
                isScreen={false}
                layout={layout}
              />
            </View>
          ) : (
            <View style={styles.container}>
              <TouchableOpacity
                onPress={() => {
                  onPress(getActions(attrs.actions), {});
                }}
                style={{
                  minHeight: attrs.height,
                  width: attrs.width,
                }}
                activeOpacity={1}
              >
                <ObjectRender
                  keyItem={attrs?.selectedItem?.itemIndex}
                  arrComp={arrCom}
                  isScreen={false}
                  layout={layout}
                />
              </TouchableOpacity>
            </View>
          )}
        </GroupScrollableWrapper>
      </>
    );
  }
};

export default CusGroup;
